import { shallowRef, watch } from 'vue';

import { propsFactory } from '@components/utils';
import { TABLE_INDENT_SIZE } from '@components/components/CdsTable/constants';

import { CdsTooltip } from '../../CdsTooltip';

import type { NTable } from '../interface';
import type { ComponentProps } from '@components/utils';
import type { PropType, Ref } from 'vue';

export interface ITableProps<RecordType = NTable.TDefaultRecord> {
  id?: string;
  data?: RecordType[];
  columns?: NTable.NColumn.TColumns<RecordType>;
  rowKey?: string | NTable.NRow.TGetRowKey<RecordType>;
  rowClassName?: string | NTable.NRow.TClassName<RecordType>;
  tableLayout?: NTable.TLayout;
  showHeader?: boolean;
  sticky?: boolean | NTable.NSticky.ISticky;
  loading?: boolean;
  hovered?: boolean;
  bordered?: boolean;
  borderless?: boolean;
  header?: NTable.NRender.TPanelRender<RecordType>;
  footer?: NTable.NRender.TPanelRender<RecordType>;
  components?: NTable.NCustomize.TableComponents<RecordType>;
  customRow?: NTable.NCustomize.TGetComponentProps<RecordType>;
  customHeaderRow?: NTable.NCustomize.TGetComponentProps<NTable.NColumn.IColumn<RecordType>[]>;
  pagination?: false | NTable.NPagination.IProps;
  scroll?: NTable.TScroll;
  sortDirections?: NTable.TSortOrder[];
  showSorterTooltip?: boolean | ComponentProps<typeof CdsTooltip>;
  expandFixed?: NTable.TFixed;
  expandColumnWidth?: number;
  expandedRowKeys?: NTable.TKey[];
  expandIconColumnIndex?: number;
  rowSelection?: NTable.NRow.IRowSelection<RecordType>;
  defaultExpandedRowKeys?: NTable.TKey[];
  defaultExpandAllRows?: boolean;
  canExpandable?: boolean;
  expandedRowRender?: NTable.NExpand.TExpandedRowRender<RecordType>;
  expandRowByClick?: boolean;
  expandIcon?: NTable.NExpand.TRenderExpandIcon<RecordType>;
  indentSize: number;
  showExpandColumn?: boolean;
  expandedRowClassName?: NTable.NRow.TClassName<RecordType>;
  childrenColumnName?: string;
  rowExpandable?: NTable.NExpand.IExpandable['rowExpandable'];
  transformCellText?: NTable.NCell.TTransformCellText<RecordType>;
  getPopupContainer?: NTable.NCustomize.TGetPopupContainer;

  // =================================== Events ===================================
  onChange?: (
    pagination: NTable.NPagination.IProps,
    filters: Record<string, NTable.TFilterValue | null>,
    sorter: NTable.ISorterResult | NTable.ISorterResult[],
    extra: NTable.ICurrentData
  ) => void;
  onExpand?: (expanded: boolean, record: RecordType) => void;
  onExpandedRowsChange?: (expandedKeys: NTable.TKey[]) => void;
  onResizeColumn?: (width: number, column: NTable.NColumn.IColumn<RecordType>) => void;

  // =================================== Internal ===================================
  /**
   * @private Internal usage, may remove by refactor. Should always use `columns` instead.
   *
   * !!! DO NOT USE IN PRODUCTION ENVIRONMENT !!!
   */
  internalHooks?: string;

  /**
   * @private Internal usage, may remove by refactor. Should always use `columns` instead.
   *
   * !!! DO NOT USE IN PRODUCTION ENVIRONMENT !!!
   */
  // Used for internal table transform column with additional column
  transformColumns?: (columns: NTable.NColumn.TColumns<RecordType>) => NTable.NColumn.TColumns<RecordType>;

  /**
   * @private Internal usage, may remove by refactor.
   *
   * !!! DO NOT USE IN PRODUCTION ENVIRONMENT !!!
   */
  internalRefs?: {
    body: HTMLDivElement | null;
  };

  /**
   * @private Internal usage, may remove by refactor.
   *
   * !!! DO NOT USE IN PRODUCTION ENVIRONMENT !!!
   */
  onUpdateInternalRefs?: (refs: Record<string, any>) => void;
}

export const makeTableProps = propsFactory(
  {
    /**
     * Идентификатор таблицы
     */
    id: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Массив данных для отображения в таблице
     */
    data: {
      type: Array as PropType<NTable.TDefaultRecord[]>,
      default: undefined
    },
    /**
     * Колонки таблицы
     */
    columns: {
      type: Array as PropType<NTable.NColumn.TColumns<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Уникальный ключ строки может быть строкой или функцией возвращающей строку
     */
    rowKey: {
      type: [String, Function] as PropType<string | NTable.NRow.TGetRowKey<NTable.TDefaultRecord>>,
      default: 'key'
    },
    /**
     * Классы строки
     */
    rowClassName: {
      type: [String, Function] as PropType<string | NTable.NRow.TClassName<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Фиксированный или авто лэйаут
     */
    tableLayout: {
      type: String as PropType<NTable.TLayout>,
      default: undefined
    },
    /**
     * Показывать ли заголовок таблицы
     */
    showHeader: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Sticky для заголовка и скроллбара
     */
    sticky: {
      type: [Boolean, Object] as PropType<boolean | NTable.NSticky.ISticky>,
      default: undefined
    },
    /**
     * Загрузка таблицы
     */
    loading: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Подсвечивание строки при наведении
     */
    hovered: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Показывать все границы таблицы
     */
    bordered: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Убрать все границы таблицы
     */
    borderless: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Рендер заголовка таблицы
     */
    header: {
      type: Function as PropType<NTable.NRender.TPanelRender<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Рендер тулбара таблицы
     */
    toolbar: {
      type: Function as PropType<NTable.NRender.TPanelRender<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Рендер футера таблицы
     */
    footer: {
      type: Function as PropType<NTable.NRender.TPanelRender<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Переопределить компоненты таблицы по умолчанию
     */
    components: {
      type: Object as PropType<NTable.NCustomize.TableComponents<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Устанавливает свойства для каждой строки
     */
    customRow: {
      type: Function as PropType<NTable.NCustomize.TGetComponentProps<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Устанавливает свойства для каждой строки заголовка
     */
    customHeaderRow: {
      type: Function as PropType<NTable.NCustomize.TGetComponentProps<NTable.NColumn.IColumn<NTable.TDefaultRecord>[]>>,
      default: undefined
    },
    /**
     * Конфигурация пагинации таблицы
     */
    pagination: {
      type: [Object, Boolean] as PropType<false | NTable.NPagination.IProps>,
      default: undefined
    },
    /**
     * Можно ли скроллить таблицу
     */
    scroll: {
      type: Object as PropType<NTable.TScroll & { scrollToFirstRowOnChange?: boolean }>,
      default: undefined
    },
    /**
     * Сортировка данных в таблице. 'ascend' | 'descend'
     */
    sortDirection: {
      type: Array as PropType<NTable.TSortOrder[]>,
      default: undefined
    },
    /**
     * Показывать ли тултипы для сортировки в заголовке. Можно передать объект со свойствами для CdsTooltip.
     */
    showSorterTooltip: {
      type: [Boolean, Object] as PropType<boolean | ComponentProps<typeof CdsTooltip>>,
      default: true
    },
    /**
     * Сделать колонку с раскрытием фиксированной (left | right | boolean)
     */
    expandFixed: {
      type: [Boolean, String] as PropType<NTable.TFixed>,
      default: undefined
    },
    /**
     * Ширина колонки с раскрытием
     */
    expandColumnWidth: {
      type: Number as PropType<number>,
      default: undefined
    },
    /**
     * Ключи текущих раскрытых строк (поддерживает v-model)
     */
    expandedRowKeys: {
      type: Array as PropType<NTable.TKey[]>,
      default: undefined as ITableProps['expandedRowKeys']
    },
    /**
     * Конфигурация для выбора строк
     */
    rowSelection: {
      type: Object as PropType<NTable.NRow.IRowSelection<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Ключи раскрытых строк по умолчанию
     */
    defaultExpandedRowKeys: {
      type: Array as PropType<NTable.TKey[]>,
      default: undefined as ITableProps['defaultExpandedRowKeys']
    },
    /**
     * По умолчанию все колонки раскрыты
     */
    defaultExpandAllRows: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Может раскрываться
     */
    canExpandable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Рендер для каждой раскрытой строки
     */
    expandedRowRender: {
      type: Function as PropType<NTable.NExpand.TExpandedRowRender<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Раскрытие строки по клику в любое место этой строки
     */
    expandRowByClick: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Индекс колонки, в которой будет располагаться иконка раскрытия
     */
    expandIconColumnIndex: {
      type: Number as PropType<number>,
      default: undefined
    },
    /**
     * Своя иконка раскрытия
     */
    expandIcon: {
      type: Function as PropType<NTable.NExpand.TRenderExpandIcon<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Размер отступа в пикселях для каждого уровня вложенности
     */
    indentSize: {
      type: Number as PropType<number>,
      default: TABLE_INDENT_SIZE
    },
    /**
     * Показать раскрытую колонку
     */
    showExpandColumn: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    /**
     * Классы для строк в раскрытом состоянии
     */
    expandedRowClassName: {
      type: Function as PropType<NTable.NRow.TClassName<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Названия свойства для дочерних элементов
     */
    childrenColumnName: {
      type: String as PropType<string>,
      default: 'children'
    },
    /**
     * Строку можно раскрыть
     */
    rowExpandable: {
      type: Function as PropType<NTable.NExpand.IExpandable['rowExpandable']>,
      default: undefined
    },
    /**
     * Трансформировать текст в ячейке
     */
    transformCellText: {
      type: Function as PropType<NTable.NCell.TTransformCellText<NTable.TDefaultRecord>>,
      default: undefined
    },
    /**
     * Контейнер рендеринга выпадающих списков
     */
    getPopupContainer: {
      type: Function as PropType<NTable.NCustomize.TGetPopupContainer>,
      default: undefined
    },
    /**
     * Событие при изменении пагинации, фильтров, сортировки
     */
    onChange: {
      type: Function as PropType<
        (
          pagination: NTable.NPagination.IProps,
          filters: Record<string, NTable.TFilterValue | null>,
          sorters: NTable.ISorterResult | NTable.ISorterResult[],
          extra: NTable.ICurrentData
        ) => void
      >,
      default: undefined
    },
    /**
     * Событие изменения размера колонки
     */
    onResizeColumn: {
      type: Function as PropType<(width: number, column: NTable.NColumn.IColumn) => void>,
      default: undefined
    },
    /**
     * Событие при раскрытии строки
     */
    onExpand: {
      type: Function as PropType<(expanded: boolean, record: NTable.TDefaultRecord) => void>,
      default: undefined
    },
    /**
     * Событие при изменении раскрытых строк
     */
    onExpandedRowsChange: {
      type: Function as PropType<(expandedKeys: NTable.TKey[]) => void>,
      default: undefined
    }
  },
  'table'
);

interface IMapCache<RecordType> {
  kvMap?: Map<NTable.TKey, RecordType>;
}
export function useLazyKVMap<RecordType>(
  dataRef: Ref<readonly RecordType[]>,
  childrenColumnNameRef: Ref<string>,
  getRowKeyRef: Ref<NTable.NRow.TGetRowKey<RecordType>>
) {
  const mapCacheRef = shallowRef<IMapCache<RecordType>>({});

  watch(
    [dataRef, childrenColumnNameRef, getRowKeyRef],
    () => {
      const kvMap = new Map<NTable.TKey, RecordType>();
      const getRowKey = getRowKeyRef.value;
      const childrenColumnName = childrenColumnNameRef.value;

      const dig = (records: readonly RecordType[]) => {
        records.forEach((record, index) => {
          const rowKey = getRowKey(record, index);
          kvMap.set(rowKey, record);

          if (record && typeof record === 'object' && childrenColumnName in record) {
            dig((record as any)[childrenColumnName] || []);
          }
        });
      };

      dig(dataRef.value);

      mapCacheRef.value = { kvMap };
    },
    { deep: true, immediate: true }
  );

  function getRecordByKey(key: NTable.TKey) {
    return mapCacheRef.value.kvMap!.get(key)!;
  }

  return { getRecordByKey };
}
